import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfigProvider,
  Dropdown,
  Empty,
  Flex,
  Input,
  MenuProps,
  Modal,
  Segmented,
  Skeleton,
  Splitter,
  Tooltip,
  Tree,
  Typography,
  message,
  theme,
} from 'antd';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Edit3,
  FileText,
  Files,
  Folder,
  FolderInput,
  FolderOpen,
  ListFilter,
  MoreVertical,
  Search,
  User,
} from 'lucide-react';
import { useAppSelector } from '@/hooks/reduxHooks';
import {
  useGenerateSasUrlMutation,
  useGetDocumentsByDealIdQuery,
  useUpdateDocumentTypeMutation,
  useUpdateDocumentNameMutation,
} from '@/store/apis/documentsApi';
import { DocumentViewer, useOcrDetail } from '@/components/DocumentViewer';
import { useParams, useSearchParams } from 'react-router-dom';
import { DOC_TYPE_OPTIONS } from '@/constant/options';
import dayjs from 'dayjs';
import { Languages } from 'lucide-react';
import { useAssessmentTranslations } from '@/pages/application/credit-analyst/hooks/useAssessmentTranslations';
import TranslationBadge from '@/pages/application/credit-analyst/pages/detail/components/TranslationBadge';
import TranslatedDocumentPreview from '../components/TranslatedDocumentPreview';
import styles from './DocumentExplorer.module.scss';

const { DirectoryTree } = Tree;

const buildDocSubtitle = (doc: any): string => {
  const parts: string[] = [];
  const dateStr = doc?.createdAt || doc?.drive?.modifiedTime;
  if (dateStr) {
    const d = dayjs(dateStr);
    if (d.isValid()) parts.push(d.format('MMM YYYY'));
  }
  const pages = doc?.pageCount ?? doc?.pages ?? doc?.drive?.pageCount;
  if (typeof pages === 'number' && pages > 0) {
    parts.push(`${pages} ${pages === 1 ? 'page' : 'pages'}`);
  }
  return parts.join(' · ');
};

interface DocumentExplorerProps {
  fullHeight?: boolean;
  hubspotDealId?: string;
  filterTypes?: string[];
  filterLabel?: string;
  defaultToFiltered?: boolean;
  assessmentId?: string;
}

const DocumentExplorer: React.FC<DocumentExplorerProps> = ({
  fullHeight,
  hubspotDealId,
  filterTypes: propsFilterTypes,
  filterLabel: propsFilterLabel,
  defaultToFiltered: propsDefaultToFiltered,
  assessmentId: propsAssessmentId,
}) => {
  const { token } = theme.useToken();

  const [searchParams, setSearchParams] = useSearchParams();
  const { dealId: dealIdFromParams } = useParams<{ dealId: string }>();
  const { dealId: dealIdFromRedux } = useAppSelector((state) => state.noteForm);

  const dealId = hubspotDealId ?? dealIdFromParams ?? dealIdFromRedux ?? '';

  const assessmentId =
    propsAssessmentId ?? searchParams.get('assessmentId') ?? undefined;
  const {
    byDoc: translationByDoc,
    translateDocuments,
    openTranslatedPdf,
    fetchTranslatedObjectUrl,
    openingDocId,
  } = useAssessmentTranslations(assessmentId);

  const filterTypes = useMemo(() => {
    if (propsFilterTypes && propsFilterTypes.length > 0)
      return propsFilterTypes;
    return searchParams.getAll('type');
  }, [propsFilterTypes, searchParams]);

  const filterLabel =
    propsFilterLabel || searchParams.get('label') || 'Filtered';
  const docIdFromUrl = searchParams.get('docId');
  const defaultToFiltered =
    propsDefaultToFiltered ?? searchParams.get('isFiltered') === 'true';
  const hasFilter = filterTypes.length > 0;

  const [filterMode, setFilterMode] = useState<'all' | 'filtered'>(
    hasFilter && defaultToFiltered ? 'filtered' : 'all',
  );
  const [searchValue, setSearchValue] = useState('');
  const [sasUrl, setSasUrl] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [sizes, setSizes] = useState<(number | string)[]>(['25%', '75%']);

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [activeNode, setActiveNode] = useState<any>(null);
  const [tempName, setTempName] = useState('');
  // Search query for the "Move to category" submenu — reset when it closes.
  const [moveSearch, setMoveSearch] = useState('');

  const [generateSasUrl, { isLoading: isGenerateSasUrlLoading }] =
    useGenerateSasUrlMutation();

  const selectedFileName =
    selectedDocument?.drive?.name ??
    selectedDocument?.blob?.blobName ??
    undefined;
  const ocrDetail = useOcrDetail(selectedDocument?.id);
  const [updateDocumentName, { isLoading: isUpdatingName }] =
    useUpdateDocumentNameMutation();
  const [updateDocumentType] = useUpdateDocumentTypeMutation();

  const { data: docsData, isLoading: isDocsLoading } =
    useGetDocumentsByDealIdQuery({ hubspotDealId: dealId }, { skip: !dealId });

  const totalDocCount = useMemo(() => {
    if (!docsData?.data?.groups) return 0;
    return docsData.data.groups.reduce(
      (sum: number, group: any) =>
        sum + ((group.documents as any[])?.length ?? 0),
      0,
    );
  }, [docsData]);

  useEffect(() => {
    if (!selectedDocument || !docsData?.data?.groups) return;
    for (const group of docsData.data.groups) {
      const doc = (group.documents as any[])?.find(
        (d: any) => d.id === selectedDocument.id,
      );
      if (doc) {
        const email = group?.dealContact?.email ?? 'Unknown contact';
        const docType = doc.docType ?? 'OTHER';
        const contactKey = `contact-${group.groupKey ?? email}`;
        const typeKey = `type-${email}-${docType}`;
        setExpandedKeys((prev) => {
          const keys = new Set(prev);
          keys.add(contactKey);
          keys.add(typeKey);
          return Array.from(keys);
        });
        break;
      }
    }
  }, [selectedDocument, docsData]);

  useEffect(() => {
    if (!docIdFromUrl || !docsData?.data?.groups) return;
    for (const group of docsData.data.groups) {
      const doc = (group.documents as any[])?.find(
        (d: any) => d.id === docIdFromUrl,
      );
      if (doc) {
        setSelectedDocument(doc);
        if (doc.blob?.container && doc.blob?.blobName) {
          generateSasUrl({
            containerName: doc.blob.container,
            blobName: doc.blob.blobName,
          })
            .unwrap()
            .then((res: any) => setSasUrl(res?.data?.url ?? null));
        }
        break;
      }
    }
  }, [docIdFromUrl, docsData]);

  const treeData = useMemo(() => {
    if (!docsData?.data?.groups) return [];

    return docsData.data.groups
      .map((group: any) => {
        const email = group?.dealContact?.email ?? 'Unknown contact';
        const documents = (group?.documents ?? []) as any[];
        const typeMap: Record<string, any[]> = {};

        documents.forEach((d) => {
          const type = d.docType ?? 'OTHER';

          if (filterMode === 'filtered' && hasFilter) {
            const normalizedType = type.replace(/_/g, ' ');
            const isMatch = filterTypes.some(
              (ft) =>
                ft.toUpperCase() === type.toUpperCase() ||
                ft.toUpperCase() === normalizedType.toUpperCase(),
            );
            if (!isMatch) return;
          }

          if (!typeMap[type]) typeMap[type] = [];
          typeMap[type].push(d);
        });

        if (Object.keys(typeMap).length === 0) return null;

        const contactDocCount = Object.values(typeMap).reduce(
          (sum, docs) => sum + docs.length,
          0,
        );

        return {
          title: email,
          key: `contact-${group.groupKey ?? email}`,
          nodeType: 'contact',
          count: contactDocCount,
          children: Object.entries(typeMap).map(([docType, docs]) => ({
            title: docType.replace(/_/g, ' '),
            key: `type-${email}-${docType}`,
            nodeType: 'category',
            count: docs.length,
            children: docs.map((doc) => ({
              title:
                doc?.drive?.name || doc?.blob?.blobName || 'Untitled Document',
              key: doc.id,
              isLeaf: true,
              doc: doc,
              subtitle: buildDocSubtitle(doc),
            })),
          })),
        };
      })
      .filter(Boolean);
  }, [docsData, filterMode, filterTypes, hasFilter]);

  const filteredTreeData = useMemo(() => {
    if (!searchValue) return treeData;
    const filterNodes = (nodes: any[], isParentMatch = false): any[] => {
      return nodes
        .map((node) => {
          const titleStr = String(node.title).toLowerCase();
          const target = searchValue.toLowerCase();
          const currentMatches = titleStr.includes(target);
          const shouldForceIncludeChildren = isParentMatch || currentMatches;

          if (node.children) {
            const filteredChildren = filterNodes(
              node.children,
              shouldForceIncludeChildren,
            );
            if (currentMatches || filteredChildren.length > 0) {
              return { ...node, children: filteredChildren };
            }
          }
          return currentMatches || isParentMatch ? node : null;
        })
        .filter(Boolean);
    };
    return filterNodes(treeData);
  }, [treeData, searchValue]);

  // All expandable (branch) keys for the current tree — used by expand/collapse
  // all and to reveal search matches that would otherwise stay hidden.
  const allBranchKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const walk = (nodes: any[]) =>
      nodes.forEach((node) => {
        if (node.children?.length) {
          keys.push(node.key);
          walk(node.children);
        }
      });
    walk(treeData);
    return keys;
  }, [treeData]);

  // Expand every accordion once documents first load so the tree opens fully.
  const didInitialExpandRef = useRef(false);
  useEffect(() => {
    if (didInitialExpandRef.current) return;
    if (allBranchKeys.length === 0) return;
    setExpandedKeys(allBranchKeys);
    setAutoExpandParent(false);
    didInitialExpandRef.current = true;
  }, [allBranchKeys]);

  // If the filtered view has no documents, fall back to showing all documents.
  useEffect(() => {
    if (filterMode !== 'filtered') return;
    if (!docsData?.data?.groups) return;
    if (treeData.length === 0) setFilterMode('all');
  }, [filterMode, treeData, docsData]);

  const visibleDocCount = useMemo(() => {
    let count = 0;
    const walk = (nodes: any[]) =>
      nodes.forEach((node) =>
        node.isLeaf ? (count += 1) : node.children && walk(node.children),
      );
    walk(filteredTreeData);
    return count;
  }, [filteredTreeData]);

  const allExpanded =
    allBranchKeys.length > 0 && expandedKeys.length >= allBranchKeys.length;

  const toggleExpandAll = () => {
    setAutoExpandParent(false);
    setExpandedKeys(allExpanded ? [] : allBranchKeys);
  };

  // Searching should reveal matches: expand every branch that survived the
  // filter so results aren't buried under collapsed contacts/categories.
  useEffect(() => {
    if (!searchValue) return;
    const keys: React.Key[] = [];
    const walk = (nodes: any[]) =>
      nodes.forEach((node) => {
        if (node.children?.length) {
          keys.push(node.key);
          walk(node.children);
        }
      });
    walk(filteredTreeData);
    setExpandedKeys(keys);
    setAutoExpandParent(true);
  }, [searchValue, filteredTreeData]);

  const onSelect = (_selectedKeys: any[], info: any) => {
    const node = info.node;
    if (node.isLeaf && node.doc) {
      setSelectedDocument(node.doc);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('docId', node.doc.id);
        return next;
      });
      if (node.doc.blob?.container && node.doc.blob?.blobName) {
        generateSasUrl({
          containerName: node.doc.blob.container,
          blobName: node.doc.blob.blobName,
        })
          .unwrap()
          .then((res: any) => setSasUrl(res?.data?.url ?? null));
      }
    }
  };

  const handleCloseDocument = () => {
    setSelectedDocument(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('docId');
      return next;
    });
  };

  const handleMenuClick = (key: string, node: any, e: any) => {
    if (e && e.domEvent) e.domEvent.stopPropagation();
    setActiveNode(node);
    if (key === 'rename') {
      setTempName(node.title);
      setIsRenameModalOpen(true);
    } else if (key.startsWith('translate:')) {
      const mode = key.slice('translate:'.length) as 'reconstruct' | 'overlay';
      if (node.doc?.id) translateDocuments([node.doc.id], mode);
    } else if (key === 'open-translated') {
      const t = translationByDoc.get(node.doc?.id);
      if (t) openTranslatedPdf(t);
    } else if (key.startsWith('move:')) {
      const type = key.slice('move:'.length);
      if (!type || type === node.doc?.docType) return;
      updateDocumentType({ documentId: node.key, type })
        .unwrap()
        .then(() => message.success('Document moved successfully'))
        .catch(() => message.error('Could not move document'));
    }
  };

  const getContextMenuItems = (node: any): MenuProps['items'] => {
    const currentType = node?.doc?.docType ?? '';

    // Translation actions — only when the explorer knows the assessment.
    const t = assessmentId ? translationByDoc.get(node?.doc?.id) : undefined;
    const inFlight = t?.status === 'QUEUED' || t?.status === 'RUNNING';
    const translationItems: MenuProps['items'] = assessmentId
      ? [
          { type: 'divider' },
          t?.status === 'COMPLETED'
            ? {
                key: 'open-translated',
                label: 'Open translated PDF',
                icon: <Languages size={14} />,
              }
            : {
                key: 'translate',
                label: inFlight ? 'Translating…' : 'Translate to English',
                icon: <Languages size={14} />,
                disabled: inFlight,
                children: inFlight
                  ? undefined
                  : [
                      {
                        key: 'translate:reconstruct',
                        label: 'Reconstruct (default)',
                      },
                      { key: 'translate:overlay', label: 'Overlay on scan' },
                    ],
              },
        ]
      : [];

    return [
      {
        key: 'doc-header',
        type: 'group',
        label: (
          <div className={styles.menuHeader}>
            <FileText size={13} style={{ flexShrink: 0 }} />
            <span className={styles.menuHeaderText}>{node.title}</span>
          </div>
        ),
      },
      { type: 'divider' },
      { label: 'Rename', key: 'rename', icon: <Edit3 size={14} /> },
      ...translationItems,
      {
        label: 'Move to category',
        key: 'move',
        icon: <FolderInput size={14} />,
        popupClassName: styles.moveSubmenu,
        children: [
          {
            key: 'move-search',
            className: styles.moveSearchItem,
            label: (
              <Input
                size="small"
                autoFocus
                allowClear
                placeholder="Search categories…"
                prefix={
                  <Search
                    size={12}
                    style={{ color: token.colorTextTertiary }}
                  />
                }
                value={moveSearch}
                onChange={(e) => setMoveSearch(e.target.value)}
                // Keep typing/clicking inside the input from closing the menu.
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            ),
          },
          { type: 'divider' },
          ...(() => {
            const q = moveSearch.trim().toLowerCase();
            const matches = q
              ? DOC_TYPE_OPTIONS.filter(
                  (opt) =>
                    opt.label.toLowerCase().includes(q) ||
                    opt.value.toLowerCase().includes(q),
                )
              : DOC_TYPE_OPTIONS;

            if (matches.length === 0) {
              return [
                {
                  key: 'move-empty',
                  label: 'No matching categories',
                  disabled: true,
                },
              ];
            }

            return matches.map((opt) => {
              const isCurrent = opt.value === currentType;
              return {
                key: `move:${opt.value}`,
                label: opt.label,
                disabled: isCurrent,
                icon: isCurrent ? (
                  <Check size={14} style={{ color: token.colorPrimary }} />
                ) : (
                  <span style={{ display: 'inline-block', width: 14 }} />
                ),
              };
            });
          })(),
        ],
      },
    ];
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Tree: {
            directoryNodeSelectedBg: token.colorPrimaryBg,
            directoryNodeSelectedColor: token.colorPrimary,
            nodeHoverBg: token.colorFillTertiary,
            indentSize: 20,
          },
        },
      }}
    >
      <Card
        size="small"
        style={{
          borderRadius: 12,
          height: fullHeight ? '100%' : '100vh',
          overflow: 'hidden',
        }}
        styles={{ body: { padding: 0, height: '100%' } }}
      >
        <Splitter
          onResize={setSizes}
          style={{ height: '100%', background: token.colorBgContainer }}
        >
          {/* ─── Left: Tree Panel ─── */}
          <Splitter.Panel
            collapsible
            size={sizes[0]}
            style={{
              borderRight: `1px solid ${token.colorBorderSecondary}`,
              overflow: 'hidden',
            }}
          >
            <div className={styles.docPanel}>
              {/* Header */}
              <div className={styles.panelHeader}>
                <Flex align="center" gap={8}>
                  <Files
                    size={15}
                    style={{ color: token.colorPrimary, flexShrink: 0 }}
                  />
                  <Typography.Text
                    strong
                    style={{ fontSize: 13, color: token.colorTextHeading }}
                  >
                    Documents
                  </Typography.Text>
                  {!isDocsLoading && totalDocCount > 0 && (
                    <Badge
                      count={totalDocCount}
                      overflowCount={999}
                      style={{
                        backgroundColor: token.colorFillSecondary,
                        color: token.colorTextSecondary,
                        boxShadow: 'none',
                        fontSize: 10,
                      }}
                    />
                  )}
                </Flex>
                {!isDocsLoading && allBranchKeys.length > 0 && (
                  <Tooltip
                    title={allExpanded ? 'Collapse all' : 'Expand all'}
                    placement="left"
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={
                        allExpanded ? (
                          <ChevronsDownUp size={14} />
                        ) : (
                          <ChevronsUpDown size={14} />
                        )
                      }
                      onClick={toggleExpandAll}
                      style={{ color: token.colorTextTertiary }}
                    />
                  </Tooltip>
                )}
              </div>

              {/* Filter & Search */}
              <div className={styles.filterBar}>
                {hasFilter && (
                  <Segmented
                    block
                    value={filterMode}
                    onChange={(val) => setFilterMode(val as 'all' | 'filtered')}
                    options={[
                      { label: 'All Docs', value: 'all' },
                      {
                        label: filterLabel,
                        value: 'filtered',
                        icon: <ListFilter size={13} />,
                      },
                    ]}
                  />
                )}
                <Input
                  placeholder="Search files..."
                  prefix={
                    <Search
                      size={13}
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  allowClear
                  variant="filled"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
                {!isDocsLoading &&
                  (searchValue || filterMode === 'filtered') &&
                  totalDocCount > 0 && (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 11, paddingLeft: 2 }}
                    >
                      Showing {visibleDocCount} of {totalDocCount}
                    </Typography.Text>
                  )}
              </div>

              {/* Tree */}
              <div className={styles.treeWrap}>
                {isDocsLoading ? (
                  <div className={styles.loadingState}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        active
                        paragraph={false}
                        title={{ width: i % 2 === 0 ? '85%' : '60%' }}
                      />
                    ))}
                  </div>
                ) : filteredTreeData.length > 0 ? (
                  <DirectoryTree
                    blockNode
                    treeData={filteredTreeData}
                    onSelect={onSelect}
                    selectedKeys={selectedDocument ? [selectedDocument.id] : []}
                    expandedKeys={expandedKeys}
                    autoExpandParent={autoExpandParent}
                    onExpand={(keys) => {
                      setExpandedKeys(keys);
                      setAutoExpandParent(false);
                    }}
                    expandAction="click"
                    // Tree-level icon renderer — overrides DirectoryTree defaults
                    icon={(nodeProps: any) => {
                      if (nodeProps.isLeaf) {
                        return (
                          <FileText
                            size={14}
                            style={{
                              color: nodeProps.selected
                                ? token.colorText
                                : token.colorTextTertiary,
                            }}
                          />
                        );
                      }
                      if (nodeProps.nodeType === 'contact') {
                        return (
                          <User
                            size={13}
                            style={{ color: token.colorTextTertiary }}
                          />
                        );
                      }
                      return nodeProps.expanded ? (
                        <FolderOpen
                          size={13}
                          style={{ color: token.colorPrimary }}
                        />
                      ) : (
                        <Folder
                          size={13}
                          style={{ color: token.colorTextQuaternary }}
                        />
                      );
                    }}
                    switcherIcon={(props: any) =>
                      props.expanded ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronRight size={13} />
                      )
                    }
                    titleRender={(node: any) => {
                      const isLeaf = node.isLeaf;
                      const isCategory = node.nodeType === 'category';
                      const isSelected =
                        isLeaf && selectedDocument?.id === node.key;

                      const titleStyle: React.CSSProperties = isCategory
                        ? {
                            fontSize: 11.5,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: token.colorTextHeading,
                          }
                        : isLeaf
                          ? {
                              fontSize: 12.5,
                              fontWeight: isSelected ? 600 : 500,
                              color: token.colorText,
                            }
                          : {
                              fontSize: 13,
                              fontWeight: 600,
                              color: token.colorTextHeading,
                            };

                      const nodeContent = (
                        <div className={styles.treeNodeRow}>
                          <div className={styles.treeNodeMain}>
                            <Typography.Text
                              ellipsis
                              style={{ ...titleStyle, display: 'block' }}
                            >
                              {node.title}
                            </Typography.Text>
                            {isLeaf && node.subtitle && (
                              <Typography.Text
                                ellipsis
                                className={styles.leafSubtitle}
                                style={{ color: token.colorTextTertiary }}
                              >
                                {node.subtitle}
                              </Typography.Text>
                            )}
                          </div>

                          <Flex
                            align="center"
                            gap={4}
                            style={{ flexShrink: 0 }}
                          >
                            {!isLeaf && node.count > 0 && (
                              <Badge
                                count={node.count}
                                overflowCount={999}
                                style={{
                                  backgroundColor: token.colorFillSecondary,
                                  color: token.colorTextSecondary,
                                  boxShadow: 'none',
                                  fontSize: 10,
                                }}
                              />
                            )}
                            {isLeaf &&
                              assessmentId &&
                              translationByDoc.get(node.key) && (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <TranslationBadge
                                    translation={translationByDoc.get(node.key)}
                                    onOpen={openTranslatedPdf}
                                    isOpening={openingDocId === node.key}
                                  />
                                </div>
                              )}
                            {isLeaf && (
                              <Dropdown
                                menu={{
                                  items: getContextMenuItems(node),
                                  onClick: (e) =>
                                    handleMenuClick(e.key, node, e),
                                }}
                                overlayClassName={styles.contextMenu}
                                trigger={['click']}
                                onOpenChange={(open) =>
                                  !open && setMoveSearch('')
                                }
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  className="doc-tree-menu-btn"
                                  icon={<MoreVertical size={13} />}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: token.colorTextTertiary }}
                                />
                              </Dropdown>
                            )}
                          </Flex>
                        </div>
                      );

                      if (!isLeaf) return nodeContent;

                      return (
                        <Dropdown
                          menu={{
                            items: getContextMenuItems(node),
                            onClick: (e) => handleMenuClick(e.key, node, e),
                          }}
                          overlayClassName={styles.contextMenu}
                          trigger={['contextMenu']}
                          onOpenChange={(open) => !open && setMoveSearch('')}
                        >
                          {nodeContent}
                        </Dropdown>
                      );
                    }}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {!dealId
                          ? 'No deal selected'
                          : searchValue
                            ? 'No files match your search'
                            : filterMode === 'filtered'
                              ? `No documents in ${filterLabel}`
                              : 'No documents found'}
                      </Typography.Text>
                    }
                    style={{ marginTop: 40 }}
                  />
                )}
              </div>
            </div>
          </Splitter.Panel>

          {/* ─── Right: Preview Panel ─── */}
          <Splitter.Panel
            size={sizes[1]}
            style={{
              position: 'relative',
              background: token.colorBgLayout,
            }}
          >
            <div style={{ height: '100%' }}>
              {selectedDocument ? (
                assessmentId ? (
                  <TranslatedDocumentPreview
                    key={selectedDocument.id}
                    fileName={selectedFileName}
                    sasUrl={sasUrl}
                    isGenerateSasUrlLoading={isGenerateSasUrlLoading}
                    onClose={handleCloseDocument}
                    translation={translationByDoc.get(selectedDocument.id)}
                    fetchTranslatedObjectUrl={fetchTranslatedObjectUrl}
                  />
                ) : (
                  <DocumentViewer
                    url={sasUrl}
                    isLoading={isGenerateSasUrlLoading}
                    fileName={selectedFileName}
                    contentType={
                      selectedDocument?.blob?.contentType ??
                      selectedDocument?.drive?.mimeType
                    }
                    onClose={handleCloseDocument}
                    detail={ocrDetail}
                    emptyDescription="A preview isn't available for this document yet - it may still be processing. Try again once processing completes, or open it in Google Drive."
                  />
                )
              ) : (
                <div className={styles.emptyPreview}>
                  <div className={styles.emptyPreviewIcon}>
                    <FileText
                      size={26}
                      style={{ color: token.colorTextQuaternary }}
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Typography.Text
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 500,
                        color: token.colorTextSecondary,
                        marginBottom: 4,
                      }}
                    >
                      No file selected
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Select a document from the panel to preview it here
                    </Typography.Text>
                  </div>
                </div>
              )}
            </div>
          </Splitter.Panel>
        </Splitter>
      </Card>

      <Modal
        title="Rename Document"
        open={isRenameModalOpen}
        onCancel={() => setIsRenameModalOpen(false)}
        loading={isUpdatingName}
        onOk={() => {
          updateDocumentName({ documentId: activeNode.key, name: tempName })
            .unwrap()
            .then(() => {
              message.success('Document renamed successfully');
              setIsRenameModalOpen(false);
            });
        }}
      >
        <div style={{ padding: '12px 0' }}>
          <Input
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            autoFocus
          />
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default DocumentExplorer;
